import { RemediationItemStatus, RemediationRunStatus } from '@libs/constants';

/**
 * Per-document progress for the live run screen.
 *
 * **Everything here is measured, nothing is modelled.**
 *
 * An earlier version interpolated a five-stage pipeline and a "~N min left"
 * countdown from wall-clock elapsed time, because the API reported neither.
 * Real runtimes are dominated by LLM retry tails rather than document count — a
 * single generation call can burn 12 HTTP attempts at a 120s timeout each
 * (`LLM_MAX_RETRIES`), and observed provider latencies range from 25s to 460s
 * for the same model — so no per-document constant survives the spread, and the
 * countdown was routinely off by multiples.
 *
 * The per-document signal that *is* real arrived with the progress callbacks: the
 * remediation Job now posts `started` / `completed` / `failed` per item as it
 * works, landing on `aiStartedAt` / `aiCompletedAt`. That is what these states
 * read. Both timestamps are null on runs that predate it and on any run whose
 * Job could not reach the callback URL, and the states below degrade to the older
 * coarse behaviour rather than claiming anything: null means unknown.
 */

export type ItemProgressState = 'queued' | 'processing' | 'done' | 'failed';

export interface ItemProgress {
  state: ItemProgressState;
  /** When the AI picked this document up, for a per-document elapsed reading. */
  startedAt: string | null;
}

/**
 * Statuses where the AI produced a result — a document to review, or a considered
 * "nothing to change here".
 *
 * Note what is absent: FAILED. A failed document is finished, but it is not a
 * result, and the two readings want different lists — see
 * {@link FINISHED_ITEM_STATUSES}.
 */
export const DELIVERED_ITEM_STATUSES: readonly RemediationItemStatus[] = [
  RemediationItemStatus.REVIEW_PENDING,
  RemediationItemStatus.NO_CHANGES,
  RemediationItemStatus.ACCEPTED,
  RemediationItemStatus.REJECTED,
];

/**
 * Statuses where the AI is done with a document one way or the other, failures
 * included. What a progress row asks before it stops showing a spinner.
 */
export const FINISHED_ITEM_STATUSES: readonly RemediationItemStatus[] = [
  ...DELIVERED_ITEM_STATUSES,
  RemediationItemStatus.FAILED,
];

/**
 * The AI has written something for this document.
 *
 * Both pre-content statuses are excluded, not just PENDING: an item is
 * PROCESSING from the moment the AI picks it up until the result payload
 * delivers what it wrote, and for that whole window it has a status but nothing
 * to show.
 */
export function hasContentLanded(status: RemediationItemStatus): boolean {
  return (
    status !== RemediationItemStatus.PENDING && status !== RemediationItemStatus.PROCESSING
  );
}

/**
 * …and there is actually something there to review. A failed document is
 * finished, but it has no content, so the review screen would render an empty
 * pane for it.
 */
export function isOpenableInReview(status: RemediationItemStatus): boolean {
  return hasContentLanded(status) && status !== RemediationItemStatus.FAILED;
}

/** Statuses carrying a decision a reviewer has already made. */
export const REVIEWED_ITEM_STATUSES: readonly RemediationItemStatus[] = [
  RemediationItemStatus.ACCEPTED,
  RemediationItemStatus.REJECTED,
  RemediationItemStatus.NO_CHANGES,
];

/**
 * One document's state, derived only from what the API actually reports.
 *
 * There is deliberately no state between "the AI finished writing it" and "you can
 * open it". Each document's content now arrives in its own callback the moment it
 * is written, so a document is reviewable within a round-trip of finishing, and a
 * fourth state would exist for less than a poll interval.
 *
 * The one inference left is `queued` vs `processing` when a run is working with no
 * timestamps at all — an older run, or a Job that could not reach the callback
 * URL. A still-PENDING item is then genuinely somewhere in the AI's queue; we do
 * not claim to know where.
 */
export function computeItemProgress(input: {
  status: RemediationItemStatus;
  /**
   * The owning run's status. Accepted for call-site compatibility and no longer
   * read — a document's state is now derived from the document alone.
   *
   * It used to collapse an undelivered item to `done` once the run left
   * PROCESSING. Appending documents made that wrong: a run waiting to be
   * reviewed can hold work queued for its next execution, and the run's status
   * says nothing about whether a given document has been written.
   */
  runStatus?: RemediationRunStatus;
  aiStartedAt?: string | null;
}): ItemProgress {
  const { status, aiStartedAt = null } = input;
  const startedAt = aiStartedAt;

  if (status === RemediationItemStatus.FAILED) return { state: 'failed', startedAt };
  if (DELIVERED_ITEM_STATUSES.includes(status)) return { state: 'done', startedAt };

  // A run past PROCESSING with an item still undelivered used to be reported as
  // done — "there is no work left to wait for, so show it as finished rather
  // than frozen". Appending documents made that false: a run waiting to be
  // reviewed can legitimately hold documents queued for the next execution, and
  // calling them finished drew a full green bar and a tick beside the word
  // "Queued". A document nobody has written is not a document that is done.
  if (status === RemediationItemStatus.PROCESSING) return { state: 'processing', startedAt };
  return { state: 'queued', startedAt };
}

/**
 * The same states, counted across a whole run from a per-status tally.
 *
 * Exists because the run screen's document list is paged by the server while the
 * stat tiles above it are whole-run figures — so the counts cannot be derived by
 * mapping {@link computeItemProgress} over the rows on screen without reporting
 * the page as the run.
 *
 * Sound because a document's state is a pure function of its `status` alone:
 * `aiStartedAt` feeds the elapsed reading and never the state, and the run's
 * status stopped being read once appending made it wrong. A tally of statuses is
 * therefore exactly as much information as the full item array — which is what
 * the spec pins down by asserting the two agree.
 *
 * Delegates each bucket to `computeItemProgress` rather than re-deciding
 * anything, so the two cannot drift.
 */
export function foldProgressCounts(
  statusCounts: Readonly<Record<RemediationItemStatus, number>>,
): Record<ItemProgressState, number> {
  const counts: Record<ItemProgressState, number> = {
    queued: 0,
    processing: 0,
    done: 0,
    failed: 0,
  };

  for (const [status, count] of Object.entries(statusCounts) as [
    RemediationItemStatus,
    number,
  ][]) {
    if (count === 0) continue;
    counts[computeItemProgress({ status }).state] += count;
  }

  return counts;
}

/**
 * Whole minutes a run has been going, from its `createdAt`. Measured, so it is
 * safe to show — unlike a remaining-time estimate, being wrong about elapsed
 * time is not possible.
 */
export function elapsedMinutes(createdAt: string, now: number): number {
  const started = new Date(createdAt).getTime();
  if (Number.isNaN(started)) return 0;
  return Math.max(0, Math.floor((now - started) / 60_000));
}

/**
 * Split elapsed minutes into hours + minutes for display. Past an hour, "73
 * min" stops being readable at a glance.
 */
export function splitElapsed(totalMinutes: number): { hours: number; minutes: number } {
  return {
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
  };
}
